import {
  EntityId,
  JobApplicationSource,
  JobApplication,
  WorkerJobPositionStatus,
  ConversationStep,
  KlaaryoConversation,
  WorkerPresentation,
  JobApplicationComplaint,
  CandidateEvaluation,
  DiscardReason,
  ListAssessmentsResponse,
  WhatsAppAssessment,
  KlaaryoAssessment,
  AssessmentMessage,
  WorkerAssessment,
  WorkerAssessmentMessage,
} from '@restworld/utility-types';
import { ENDPOINTS } from './../../endpoints';
import { useMemo } from 'react';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import {
  CreateShortlist,
  IndexConversationStepsResponse,
  IndexShortlists,
  IndexSpontaneousApplicationsResponse,
  IndexWorkerJobPositions,
  UpdateJobApplication,
  WorkerShortlistNotesGraphql,
  WorkerTimeline,
  IndexJobApplicationsParams,
  IndexJobApplicationsReturnType,
  ApplicationsCountPerCategoryType,
} from './types';
import { CreateJobApplication, Id } from '../../utils/common_types';
import useServices from '../../hooks/useServices';
import { WorkerApplyPayload, WorkerApplyStatusPayload, UpdateJobApplicationParams } from './types';
import {
  indexJobApplicationsEmployerJPPageQuery,
  indexJobApplicationsLABQuery,
  workerShortlistNotesQuery,
} from './graphqlQueries';

class ServiceWorkerJobPositions extends Service {
  fetchWorkerTimeline({ workerId, jobPositionId }: { workerId: EntityId; jobPositionId?: EntityId }) {
    return this.axiosInstance.get<WorkerTimeline>(ENDPOINTS.workerJobPositions.workerStatus, {
      params: { worker_id: workerId, job_position_id: jobPositionId },
    });
  }

  fetchShortlists({
    limit,
    offset,
    jobPositionId,
    statusKey,
    useCache,
  }: {
    limit: number;
    offset: number;
    jobPositionId?: EntityId;
    statusKey?: string;
    useCache?: boolean;
  }) {
    return this.axiosInstance.get<IndexShortlists>(ENDPOINTS.workerJobPositions.shortlists, {
      params: { limit, offset, job_position_id: jobPositionId, status_key: statusKey },
      headers: {
        useCache: !!useCache,
      },
    });
  }

  sendShortlist(shortlistId: EntityId) {
    return this.axiosInstance.post<string>(ENDPOINTS.workerJobPositions.sendShortlist, {
      shortlist_id: shortlistId,
    });
  }

  cleanPendingApplications(jobPositionId: EntityId) {
    return this.axiosInstance.get<string>(ENDPOINTS.workerJobPositions.cleanPendingApplications, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  }

  createJobApplication({
    jobPositionId,
    workerId,
    statusKey,
    source,
    params,
  }: {
    jobPositionId: string;
    workerId: string | Array<string>;
    statusKey: string;
    source?: JobApplicationSource;
    params?: Partial<JobApplication>;
  }) {
    return this.axiosInstance.post<CreateJobApplication>(ENDPOINTS.workerJobPositions.jobApplication, {
      job_position_id: jobPositionId,
      worker_id: workerId,
      status_key: statusKey,
      source,
      ...params,
    });
  }

  updateJobApplication({ jobPositionId, workerId, restaurantId, params }: UpdateJobApplicationParams) {
    return this.axiosInstance.patch<UpdateJobApplication>(ENDPOINTS.workerJobPositions.jobApplication, {
      job_position_id: jobPositionId,
      worker_id: workerId,
      restaurant_id: restaurantId,
      ...params,
    });
  }

  getDiscardReasonsForJobApplication(jobApplicationId: string) {
    return this.axiosInstance.get<DiscardReason[]>(
      `${ENDPOINTS.workerJobPositions.discardReasons}/${jobApplicationId}/discard_reasons`
    );
  }

  addWorkerToShortlist({
    shortlistId,
    workerId,
    comment,
    candidateHighlights,
  }: {
    shortlistId: EntityId;
    workerId: EntityId;
    comment: string;
    candidateHighlights?: string[];
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.workerJobPositions.addWorkerToShortlist, {
      shortlist_id: shortlistId,
      worker_id: workerId,
      notes: comment,
      candidate_highlights: candidateHighlights,
    });
  }

  deleteWorkerFromShortlist({ shortlistId, workerId }: { shortlistId: EntityId; workerId: EntityId }) {
    return this.axiosInstance.delete<string>(ENDPOINTS.workerJobPositions.removeWorkerFromShortlist, {
      data: {
        shortlist_id: shortlistId,
        worker_id: workerId,
      },
    });
  }

  updateWorkerShortlist({
    shortlistId,
    workerId,
    comment,
  }: {
    shortlistId: EntityId;
    workerId: EntityId;
    comment: string;
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.workerJobPositions.updateWorkerShortlist, {
      shortlist_id: shortlistId,
      worker_id: workerId,
      notes: comment,
    });
  }

  createShortlist({
    jobPositionId,
    workerId,
    comment,
    candidateHighlights,
  }: {
    jobPositionId: EntityId;
    workerId?: EntityId;
    comment?: string;
    candidateHighlights?: string[];
  }) {
    let workers: any = [];
    if (workerId) {
      workers = [{ worker_id: workerId, notes: comment, candidate_highlights: candidateHighlights }];
    }

    return this.axiosInstance.post<CreateShortlist>(ENDPOINTS.workerJobPositions.shortlist, {
      job_position_id: jobPositionId,
      workers,
    });
  }

  deleteShortlist(shortlistId: EntityId) {
    return this.axiosInstance.delete<string>(ENDPOINTS.workerJobPositions.shortlist, {
      data: { id: shortlistId },
    });
  }

  // send request with either status_key or sentiment
  fetchWorkerJobPositions({
    limit,
    offset,
    status_key,
    sentiment,
  }: {
    limit: number;
    offset: number;
    status_key?: string;
    sentiment?: string;
  }) {
    return this.axiosInstance.get<IndexWorkerJobPositions>(`${ENDPOINTS.workerJobPositions.jobPosition}`, {
      params: {
        limit,
        offset,
        status_key,
        sentiment,
      },
    });
  }

  getCompatibilityScore({ workerId, jobPositionId }: { workerId: string; jobPositionId: string }) {
    return this.axiosInstance.get<number>(ENDPOINTS.workerJobPositions.getCompatibilityScore, {
      params: {
        worker_id: workerId,
        job_position_id: jobPositionId,
      },
    });
  }

  jobApply(data: WorkerApplyPayload) {
    return this.axiosInstance.post<CreateJobApplication>(ENDPOINTS.workerJobPositions.workerApply, data);
  }

  jobApplyStatus(params: WorkerApplyStatusPayload) {
    return this.axiosInstance.get<JobApplication>(ENDPOINTS.workerJobPositions.jobApplyStatus, {
      params,
    });
  }

  fetchWorkerShortlistNotes(workerId: string) {
    return this.axiosInstance.post<WorkerShortlistNotesGraphql>(ENDPOINTS.workerJobPositions.graphql, {
      query: workerShortlistNotesQuery,
      variables: { worker_id: workerId },
    });
  }

  listSpontaneousApplications({ worker_id, restaurant_id }: { worker_id?: string; restaurant_id?: string }) {
    return this.axiosInstance.get<IndexSpontaneousApplicationsResponse>(
      ENDPOINTS.workerJobPositions.listSpontaneousApplications,
      {
        params: { worker_id, restaurant_id },
      }
    );
  }

  showSpontaneousApplication({ worker_id, restaurant_id }: { worker_id: string; restaurant_id: string }) {
    return this.axiosInstance.get<JobApplication>(ENDPOINTS.workerJobPositions.spontaneousApplication, {
      params: { worker_id, restaurant_id },
    });
  }

  listWorkerJobApplicationsHistory({
    jobPositionId,
    restaurantId,
    id,
    workerId,
  }: {
    jobPositionId?: string;
    restaurantId?: string;
    id?: string;
    workerId?: string;
  }) {
    return this.axiosInstance.get<WorkerJobPositionStatus[]>(ENDPOINTS.workerJobPositions.jobApplicationsHistory, {
      params: { job_position_id: jobPositionId, restaurant_id: restaurantId, id, worker_id: workerId },
    });
  }

  indexJobApplications({
    jobPositionId,
    restaurantId,
    workerId,
    id,
    limit,
    offset,
    status_key,
    last_status_change,
    show_worker_distance,
    hidden,
    spontaneous_applications,
    search_vector,
    ...rest
  }: IndexJobApplicationsParams) {
    return this.axiosInstance.get<IndexJobApplicationsReturnType>(ENDPOINTS.workerJobPositions.jobApplications, {
      params: {
        worker_id: workerId,
        job_position_id: jobPositionId,
        restaurant_id: restaurantId,
        id,
        limit,
        offset,
        status_key,
        last_status_change,
        show_worker_distance,
        hidden,
        spontaneous_applications,
        search_vector,
        ...rest,
      },
    });
  }

  indexJobApplicationsGraphql({
    jobPositionId,
    limit,
    offset,
    status_key,
    show_worker_distance,
    spontaneous_applications,
    search_vector,
    hidden,
    restaurantId,
    queryFor,
    ...rest
  }: IndexJobApplicationsParams) {
    let graphqlQuery;
    switch (queryFor) {
      case 'LAB':
        graphqlQuery = indexJobApplicationsLABQuery;
        break;
      case 'employers-JP-Page':
        graphqlQuery = indexJobApplicationsEmployerJPPageQuery;
        break;
      default:
        graphqlQuery = indexJobApplicationsEmployerJPPageQuery;
    }

    return this.axiosInstance.post<{ data: { index_job_applications: { data: JobApplication[]; hits: number } } }>(
      ENDPOINTS.workerJobPositions.graphql,
      {
        query: graphqlQuery,
        variables: {
          job_position_id: jobPositionId,
          limit,
          offset,
          status_key,
          show_worker_distance,
          spontaneous_applications,
          search_vector,
          hidden,
          restaurant_id: restaurantId,
          ...rest,
        },
      }
    );
  }

  showJobApplication({ id, jobPositionId, workerId }: { id?: string; jobPositionId?: string; workerId?: string }) {
    return this.axiosInstance.get<JobApplication>(ENDPOINTS.workerJobPositions.jobApplication, {
      params: {
        id,
        job_position_id: jobPositionId,
        worker_id: workerId,
      },
    });
  }

  fetchConversationSteps({
    limit,
    offset,
    jobApplicationId,
  }: {
    jobApplicationId: string;
    limit: number;
    offset: number;
  }) {
    return this.axiosInstance.get<IndexConversationStepsResponse>(ENDPOINTS.workerJobPositions.conversationSteps, {
      params: { job_application_id: jobApplicationId, limit, offset },
    });
  }

  createConversationStep(params: Partial<ConversationStep>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.workerJobPositions.conversationSteps, {
      ...params,
    });
  }

  updateConversationStep(params: Partial<ConversationStep>) {
    return (
      this,
      this.axiosInstance.put(`${ENDPOINTS.workerJobPositions.conversationSteps}/${params.id}`, {
        ...params,
      })
    );
  }

  deleteConversationStep(id?: string) {
    return this.axiosInstance.delete(`${ENDPOINTS.workerJobPositions.conversationSteps}/${id}`);
  }

  getInterestedWorkersCsv(jobPositionId: string) {
    return this.axiosInstance.get(`${ENDPOINTS.workerJobPositions.jobApplications}/interested_workers_csv`, {
      params: { job_position_id: jobPositionId },
      responseType: 'blob',
    });
  }

  getProposedWorkersCsv(jobPositionId: string) {
    return this.axiosInstance.get(`${ENDPOINTS.workerJobPositions.jobApplications}/proposed_workers_csv`, {
      params: { job_position_id: jobPositionId },
      responseType: 'blob',
    });
  }

  fetchKlaaryoConversation({ jobApplicationId }: { jobApplicationId: string }) {
    return this.axiosInstance.get<KlaaryoConversation>(`${ENDPOINTS.klaaryo.root}/${jobApplicationId}`);
  }

  updateKlaaryoConversation(params: Partial<KlaaryoConversation>) {
    return this.axiosInstance.patch<string>(ENDPOINTS.klaaryo.root, {
      ...params,
    });
  }

  createWorkerPresentation(params: Partial<WorkerPresentation>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.workerJobPositions.workerPresentations, {
      ...params,
    });
  }

  updateWorkerPresentation(params: Partial<WorkerPresentation>) {
    return this.axiosInstance.patch<Id>(ENDPOINTS.workerJobPositions.workerPresentations, {
      ...params,
    });
  }

  deleteWorkerPresentation(id?: number) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.workerJobPositions.workerPresentations}/${id}`);
  }

  sendUpdatedJobProposalEmails(jobPositionId: string) {
    return this.axiosInstance.post<string>(`${ENDPOINTS.workerJobPositions.root}/propose/again`, {
      job_position_id: jobPositionId,
      status: 'proposed_and_not_interested',
    });
  }

  // Worker job_application_complaints
  createJobApplicationComplaint(params: Partial<JobApplicationComplaint>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.workerJobPositions.jobApplicationComplaints, params);
  }

  showJobApplicationComplaint(id: string) {
    return this.axiosInstance.get<JobApplicationComplaint>(
      `${ENDPOINTS.workerJobPositions.jobApplicationComplaints}/${id}`
    );
  }

  updateJobApplicationComplaint(id: string, params: Partial<JobApplicationComplaint>) {
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.workerJobPositions.jobApplicationComplaints}/${id}`, params);
  }

  runEvaluation(worker_id: string, job_position_id: string) {
    return this.axiosInstance.post<CandidateEvaluation>(`${ENDPOINTS.workerJobPositions.candidateEvaluations}/run`, {
      worker_id,
      job_position_id,
    });
  }

  updateEvaluation(worker_id: string, job_position_id: string, params: Partial<CandidateEvaluation>) {
    return this.axiosInstance.patch<CandidateEvaluation>(`${ENDPOINTS.workerJobPositions.candidateEvaluations}/`, {
      worker_id,
      job_position_id,
      ...params,
    });
  }

  getJobApplicationsCountPerCategory({ jobPositionId }: { jobPositionId?: string }) {
    return this.axiosInstance.get<ApplicationsCountPerCategoryType>(
      `${ENDPOINTS.workerJobPositions.jobApplications}/count`,
      {
        params: { job_position_id: jobPositionId, category: 'job_applications_tab_LAB' },
      }
    );
  }

  // Assessment Conversations API
  listAssessmentsForWorker({
    workerId,
    limit = 20,
    offset = 0,
  }: {
    workerId: string;
    limit?: number;
    offset?: number;
  }) {
    return this.axiosInstance.get<ListAssessmentsResponse>(
      `${ENDPOINTS.workerJobPositions.assessmentsWorker}/${workerId}`,
      {
        params: { limit, offset },
      }
    );
  }

  showAssessmentConversation({ jobApplicationId }: { jobApplicationId: string }) {
    return this.axiosInstance.get<WhatsAppAssessment | KlaaryoAssessment>(
      `${ENDPOINTS.workerJobPositions.assessmentsConversation}/${jobApplicationId}`
    );
  }

  updateAssessmentConversation({
    jobApplicationId,
    managed_by_rw_member_id,
  }: {
    jobApplicationId: string;
    managed_by_rw_member_id?: string | null;
  }) {
    return this.axiosInstance.patch<WhatsAppAssessment | KlaaryoAssessment>(
      `${ENDPOINTS.workerJobPositions.assessmentsConversation}/${jobApplicationId}`,
      {
        managed_by_rw_member_id,
      }
    );
  }

  resetAssessmentConversation(jobApplicationId: string) {
    return this.axiosInstance.post('/klaaryo/reset_assessment', {
      job_application_id: jobApplicationId,
    });
  }

  sendManualAssessmentMessage({
    jobApplicationId,
    message,
    template,
    image,
    caption,
  }: {
    jobApplicationId: string;
    message?: string;
    template?: string;
    image?: File;
    caption?: string;
  }) {
    const url = `${ENDPOINTS.workerJobPositions.sendManualAssessmentMessage}/${jobApplicationId}/send_message`;

    if (image) {
      const formData = new FormData();
      formData.append('image', image);
      if (caption) formData.append('caption', caption);
      return this.axiosInstance.post<AssessmentMessage>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }

    return this.axiosInstance.post<AssessmentMessage>(url, {
      message,
      template,
    });
  }

  // Worker-facing assessment chat (in-app Serena). Addressed by job position id, which is what
  // `/candidature/:id` already carries in the worker app.
  showWorkerAssessment({ jobPositionId, before }: { jobPositionId: string; before?: string }) {
    return this.axiosInstance.get<WorkerAssessment>(
      `${ENDPOINTS.workerJobPositions.assessments}/${jobPositionId}`,
      before ? { params: { before } } : undefined
    );
  }

  /**
   * Replies 201 with the worker's own message only: Serena's answer is written later by a job.
   *
   * **The body is `{ message: … }`, not the message on its own**, exactly as the voice note
   * route answers. This was typed as the bare message and read as `response.data.id`, which is
   * `undefined` on every reply: the message never got the identity the chat reconciles by, and
   * every send silently fell back to matching on the text — the defect that had been declared
   * closed. If this type is ever flattened again the assessment chat starts mistaking one "sì"
   * for another, so it is pinned by a test.
   */
  sendWorkerAssessmentMessage({ jobPositionId, body }: { jobPositionId: string; body: string }) {
    return this.axiosInstance.post<{ message: WorkerAssessmentMessage }>(
      `${ENDPOINTS.workerJobPositions.assessments}/${jobPositionId}/messages`,
      { body }
    );
  }

  /**
   * Uploads one voice note of the worker. `multipart/form-data`, because the file is the point:
   * the backend decides the container from the first bytes, not from the declared type.
   *
   * `duration_seconds` and `levels` are what the recorder measured, and both are optional by
   * contract: a value outside the accepted domain is dropped and logged server-side, and the
   * recording is stored regardless. `levels[]` is repeated once per value, which is how a
   * Phoenix multipart parser reads a list.
   *
   * Answers 201 with `{ message: … }`, the worker's own message: the transcript lands on it
   * later and Serena's reply later still, both through the conversation read.
   */
  sendWorkerAssessmentVoiceMessage({
    jobPositionId,
    audio,
    fileName,
    durationSeconds,
    levels,
  }: {
    jobPositionId: string;
    audio: Blob;
    fileName: string;
    durationSeconds?: number;
    levels?: number[];
  }) {
    const formData = new FormData();
    formData.append('audio', audio, fileName);
    if (durationSeconds !== undefined) formData.append('duration_seconds', String(durationSeconds));
    (levels ?? []).forEach((level) => formData.append('levels[]', String(level)));

    return this.axiosInstance.post<{ message: WorkerAssessmentMessage }>(
      `${ENDPOINTS.workerJobPositions.assessments}/${jobPositionId}/voice_messages`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  /**
   * The bytes of one voice note, from the authenticated proxy. There is no signed URL and there
   * must not be one: the object on S3 is private because a candidate's own voice is kept
   * indefinitely. So an `<audio src>` pointing straight at this route would carry no JWT, and
   * the audio has to be fetched like every other authenticated call.
   */
  fetchWorkerAssessmentMessageAudio({ jobPositionId, messageId }: { jobPositionId: string; messageId: string }) {
    return this.axiosInstance.get<Blob>(
      `${ENDPOINTS.workerJobPositions.assessments}/${jobPositionId}/messages/${messageId}/audio`,
      { responseType: 'blob' }
    );
  }

  /** Start of the in-app funnel; idempotent server-side (`UPDATE … WHERE opened_at IS NULL`). */
  markWorkerAssessmentOpened({ jobPositionId }: { jobPositionId: string }) {
    return this.axiosInstance.post(`${ENDPOINTS.workerJobPositions.assessments}/${jobPositionId}/opened`);
  }
}

export function useWorkerJobPositionsService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceWorkerJobPositions(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useWorkerJobPositionsService;

export * from './types';
